import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdAdd, MdDelete, MdEdit, MdLogout, MdCloudUpload, MdVideoLibrary } from 'react-icons/md';
import { useProducts } from '../../context/ProductsContext';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { migrateLocalProductsToFirestore, getLocalProductCount } from '../../utils/migrateProductsToFirestore';
import './Admin.css';

const AdminDashboard = () => {
    const navigate = useNavigate();
    const { logout, adminUser } = useAdminAuth();
    const { getAllProducts, deleteProduct, loading } = useProducts();
    const [migrating, setMigrating] = useState(false);
    const [message, setMessage] = useState('');

    const products = getAllProducts();

    const handleDelete = async (product) => {
        if (!window.confirm(`Delete "${product.productTitle}"? This cannot be undone.`)) return;
        await deleteProduct(product.productId);
    };

    const handleMigrate = async () => {
        const localCount = getLocalProductCount();
        if (!window.confirm(`Import ${localCount} products from the bundled catalog into Firestore? Existing products with the same ID will be overwritten.`)) {
            return;
        }
        setMigrating(true);
        setMessage('');
        try {
            const count = await migrateLocalProductsToFirestore();
            setMessage(`Imported ${count} products successfully.`);
        } catch (err) {
            setMessage('Import failed: ' + err.message);
        } finally {
            setMigrating(false);
        }
    };

    let productsContent = (
        <div className="admin-product-table">
            <div className="admin-product-table-header">
                <span>Product</span>
                <span>Category</span>
                <span>Price</span>
                <span>Stock</span>
                <span>Actions</span>
            </div>
            {products.map((product) => (
                <div key={product.productId} className="admin-product-row">
                    <div className="admin-product-cell admin-product-name">
                        <img
                            src={product.images?.[0] || '/No-Image-Placeholder.svg'}
                            alt={product.productTitle}
                        />
                        <div>
                            <strong>{product.productTitle}</strong>
                            <div className="admin-hint">{product.productId}</div>
                        </div>
                    </div>
                    <span className="admin-product-cell">{product.category}</span>
                    <span className="admin-product-cell">₹{product.discountedPrice || product.originalPrice}</span>
                    <span className={`admin-product-cell ${product.inStock ? 'admin-in-stock' : 'admin-out-stock'}`}>
                        {product.inStock ? `In Stock (${product.stock ?? 0})` : 'Out of Stock'}
                    </span>
                    <div className="admin-product-cell admin-product-actions">
                        <button type="button" onClick={() => navigate(`/admin/products/${product.productId}/edit`)} aria-label="Edit product">
                            <MdEdit size={18} />
                        </button>
                        <button type="button" onClick={() => handleDelete(product)} aria-label="Delete product">
                            <MdDelete size={18} />
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
    if (loading) productsContent = <p>Loading products...</p>;
    else if (products.length === 0) productsContent = <p>No products yet. Use "Add Product" or "Import Catalog Data" to get started.</p>;

    return (
        <div className="admin-page">
            <div className="admin-dashboard-header">
                <div>
                    <h1>Seller Dashboard</h1>
                    <p className="admin-subtitle">{adminUser?.email}</p>
                </div>
                <button type="button" className="admin-btn admin-btn-outline" onClick={logout}>
                    <MdLogout size={18} /> Logout
                </button>
            </div>

            {message && <div className="admin-info-banner">{message}</div>}

            <div className="admin-toolbar">
                <button type="button" className="admin-btn admin-btn-primary" onClick={() => navigate('/admin/products/new')}>
                    <MdAdd size={18} /> Add Product
                </button>
                <button type="button" className="admin-btn admin-btn-outline" onClick={handleMigrate} disabled={migrating}>
                    <MdCloudUpload size={18} /> {migrating ? 'Importing...' : 'Import Catalog Data'}
                </button>
                <button type="button" className="admin-btn admin-btn-outline" onClick={() => navigate('/admin/videos')}>
                    <MdVideoLibrary size={18} /> Manage Videos
                </button>
            </div>

            {productsContent}
        </div>
    );
};

export default AdminDashboard;
